<script setup>
/**
 * Pulls its content a little towards the pointer, then springs back.
 *
 * For the page's main calls to action only — on every button it would be
 * noise. Desktop pointers only, and nothing under reduced motion.
 */
import { motion, useSpring, useReducedMotion } from "motion-v";
import { useMediaQuery } from "@vueuse/core";

const props = defineProps({
  // How far the content follows, as a share of the pointer's offset.
  strength: { type: Number, default: 0.22 },
});

const finePointer = useMediaQuery("(hover: hover) and (pointer: fine)");
const reduced = useReducedMotion();

const spring = { stiffness: 260, damping: 18, mass: 0.5 };
const x = useSpring(0, spring);
const y = useSpring(0, spring);

function onMove(event) {
  if (!finePointer.value || reduced.value) return;
  const rect = event.currentTarget.getBoundingClientRect();
  x.set((event.clientX - (rect.left + rect.width / 2)) * props.strength);
  y.set((event.clientY - (rect.top + rect.height / 2)) * props.strength);
}

function onLeave() {
  x.set(0);
  y.set(0);
}
</script>

<template>
  <motion.span
    class="magnetic"
    :style="{ x, y }"
    @pointermove="onMove"
    @pointerleave="onLeave"
  >
    <slot />
  </motion.span>
</template>

<style scoped>
.magnetic {
  display: inline-flex;
}
</style>
